<script setup lang="ts" generic="T extends string">
import { EKeyboardKey } from '@/utils/menu.utils';
import { useElementSize, useVirtualList } from '@vueuse/core';
import { computed, nextTick, ref, useId, watch } from 'vue';
import type { IAppearanceOption, IAppearanceOptionGroup } from '../entityAppearance.types';

const props = defineProps<{
  groups: IAppearanceOptionGroup<T>[];
  selected: T | null;
  expanded?: T | null;
  optionClass?: string;
  label: string;
  emptyLabel: string;
}>();
const emit = defineEmits<{ select: [value: T, anchor: HTMLElement]; scroll: [] }>();
defineSlots<{ option(props: { option: IAppearanceOption<T> }): void }>();

type TRow = { label: string; options: { option: IAppearanceOption<T>; index: number }[] };
const COLUMNS = 7;
const ROW_HEIGHT = 38;
const id = useId();
const activeIndex = ref(0);
const options = computed(() => props.groups.flatMap((group) => group.options));
const rows = computed<TRow[]>(() => {
  const result: TRow[] = [];
  let index = 0;
  for (const group of props.groups) {
    if (!group.options.length) continue;
    result.push({ label: group.label, options: [] });
    for (let offset = 0; offset < group.options.length; offset += COLUMNS) {
      result.push({
        label: group.label,
        options: group.options.slice(offset, offset + COLUMNS).map((option) => ({ option, index: index++ })),
      });
    }
  }
  return result;
});
const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(rows, { itemHeight: ROW_HEIGHT, overscan: 4 });
const { width: gridWidth } = useElementSize(containerProps.ref);
const rowStyle = computed(() => {
  // Even pixel tracks keep the centered glyphs and selection rings on the same pixel grid in every column.
  const columnWidth = Math.max(36, Math.floor(gridWidth.value / COLUMNS / 2) * 2);
  return {
    gridTemplateColumns: `repeat(${COLUMNS}, ${columnWidth}px)`,
    paddingLeft: `${Math.max(0, Math.floor((gridWidth.value - COLUMNS * columnWidth) / 2))}px`,
  };
});
const activeId = computed(() =>
  list.value.some((row) => row.data.options.some((entry) => entry.index === activeIndex.value))
    ? `${id}-${activeIndex.value}`
    : undefined,
);

const revealActive = (): void => {
  const rowIndex = rows.value.findIndex((row) => row.options.some((entry) => entry.index === activeIndex.value));
  if (rowIndex < 0) return;
  const element = containerProps.ref.value;
  if (!element) return;
  const top = rowIndex * ROW_HEIGHT;
  if (top < element.scrollTop || top + ROW_HEIGHT > element.scrollTop + element.clientHeight) scrollTo(rowIndex);
};
const handleKeydown = (event: KeyboardEvent): void => {
  const offsets: Partial<Record<string, number>> = {
    [EKeyboardKey.Left]: -1,
    [EKeyboardKey.Right]: 1,
    [EKeyboardKey.Up]: -COLUMNS,
    [EKeyboardKey.Down]: COLUMNS,
  };
  const offset = offsets[event.key];
  if (offset !== undefined || event.key === EKeyboardKey.Home || event.key === EKeyboardKey.End) {
    event.preventDefault();
    if (event.key === EKeyboardKey.Home) activeIndex.value = 0;
    else if (event.key === EKeyboardKey.End) activeIndex.value = options.value.length - 1;
    else if (offset !== undefined)
      activeIndex.value = Math.max(0, Math.min(options.value.length - 1, activeIndex.value + offset));
    revealActive();
  } else if (event.key === EKeyboardKey.Enter || event.key === EKeyboardKey.Space) {
    event.preventDefault();
    const anchor = containerProps.ref.value?.querySelector<HTMLElement>(`[id="${id}-${activeIndex.value}"]`);
    if (options.value.length && anchor) emit('select', options.value[activeIndex.value].value, anchor);
  }
};
const select = (entry: { option: IAppearanceOption<T>; index: number }, event: MouseEvent): void => {
  if (!(event.currentTarget instanceof HTMLElement)) return;
  activeIndex.value = entry.index;
  containerProps.ref.value?.focus({ preventScroll: true });
  emit('select', entry.option.value, event.currentTarget);
};
watch(
  () => props.groups,
  async () => {
    const selectedIndex = options.value.findIndex((option) => option.value === props.selected);
    activeIndex.value = Math.max(0, selectedIndex);
    await nextTick();
    scrollTo(0);
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-bind="containerProps"
    class="group/appearance-options min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-md outline-inner"
    role="listbox"
    :aria-label="label"
    :aria-activedescendant="activeId"
    tabindex="0"
    @keydown="handleKeydown"
    @focus="revealActive"
    @scroll="emit('scroll')"
  >
    <p v-if="!options.length" class="py-[40px] text-center text-body leading-body text-secondary" role="status">
      {{ emptyLabel }}
    </p>
    <div v-else v-bind="wrapperProps">
      <div v-for="row in list" :key="row.index" :style="{ height: `${ROW_HEIGHT}px` }" role="presentation">
        <div
          v-if="!row.data.options.length"
          class="flex h-full items-center px-xs text-footer-semibold text-secondary"
          role="presentation"
        >
          {{ row.data.label }}
        </div>
        <div v-else class="grid h-full items-center" :style="rowStyle" role="presentation">
          <button
            v-for="entry in row.data.options"
            :id="`${id}-${entry.index}`"
            :key="entry.option.value"
            type="button"
            role="option"
            tabindex="-1"
            class="mx-auto inline-flex items-center justify-center rounded-full text-secondary outline-offset-2 focus-visible:outline-focus hover:bg-highlight active:bg-highlight hover:text-tint active:text-tint"
            :class="[
              optionClass ?? 'size-icon-button',
              selected === entry.option.value && 'ring-2 ring-tint ring-inset !text-tint',
              activeIndex === entry.index && 'group-focus-visible/appearance-options:outline-focus',
              expanded === entry.option.value && 'bg-highlight',
            ]"
            :aria-label="entry.option.label"
            :title="entry.option.label"
            :aria-selected="selected === entry.option.value"
            :aria-posinset="entry.index + 1"
            :aria-setsize="options.length"
            :aria-haspopup="entry.option.hasPopup ? 'dialog' : undefined"
            :aria-expanded="entry.option.hasPopup ? expanded === entry.option.value : undefined"
            @click="select(entry, $event)"
          >
            <slot name="option" :option="entry.option"></slot>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
